import { useState, useRef, useEffect, useCallback } from 'react';
import { LottieAnimation } from '@shared/components/LottieAnimation';
import noResultsAnimation from '@/assets/lottie/noResultsAnimation.json';
import { SearchBar } from '@/shared/components/SearchBar';
import { IdentificationIcon } from '@heroicons/react/24/outline';
import UserGroupIcon from '../../../../../public/assets/Icons-Users.svg';
import { MoreVertical } from 'react-feather';
import { createPortal } from 'react-dom';
import { Menu, MenuSize } from '@/shared/components/Menu';
import { Tooltip } from '@/shared/components/Tooltip/Tooltip';
import ToggleButton from '@/shared/components/ToggleButton/ToggleButton';
import {
  listFacilityUsers,
  type FacilityUserListItem,
} from '../../api/listFacilityUserApiService';
import { EMPLOYEE_ERROR_MESSAGES } from '../../errors';
import { EditEmployeeForm } from '../EditEmployeeForm';
import { Snackbar } from '@/shared/components/Snackbar';
import { FACILITY_LABELS } from '../../constants/facilityLabels';
import { FACILITY_LABELS as TL } from '../../constants/translations/facilityLabels';
import plLabels from '../../constants/plLabels.json';
import UpdateStatusDialog from '@/epics/adminManagment/pages/adminList/UpdateStatusDialog';
import { getEmployeeStatusDialogConfig } from './employeeStatusDialogConfig';
import { updateEmployeeStatusApiService } from '../../api/updateEmployeeStatusApiService';

const LIMIT = 10;
const DEBOUNCE_MS = 300;
const MAX_HEIGHT = 480;

interface EmployeeListProps {
  facilityId: string | null;
  refreshKey: number;
  resetKey?: number;
  canEdit?: boolean;
  canToggle?: boolean;
  isToggleDisabled?: boolean;
  toggleDisabledReason?: string | undefined;
  isValid?: boolean;
  onTotalCountChange?: (count: number) => void;
  onAccessDenied?: () => void;
  onEmployeeEdited?: () => void;
  onEditingChange?: (isDirty: boolean) => void;
  onSavingChange?: (isSaving: boolean) => void;
  conflictsByEmployeeId?: Record<string, string> | undefined;
  disabled?: boolean;
}

// ─── UserCard ─────────────────────────────────────────────────────────────────

interface DropdownPosition {
  top: number;
  right: number;
}

const UserCard = ({
  user,
  conflictEmail,
  canEdit,
  canToggle,
  isToggleDisabled = false,
  toggleDisabledReason,
  onEdit,
  onToggle,
  index = 0,
}: {
  user: FacilityUserListItem;
  conflictEmail?: string | undefined;
  canEdit?: boolean;
  canToggle?: boolean;
  isToggleDisabled?: boolean;
  toggleDisabledReason?: string | undefined;
  onEdit?: () => void;
  onToggle?: (user: FacilityUserListItem) => void;
  index?: number;
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition>({
    top: 0,
    right: 0,
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const openMenu = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setIsMenuOpen(true);
  };

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };
    const handleScroll = () => setIsMenuOpen(false);

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [isMenuOpen]);

  const isActive = user.status === 'active';

  return (
    <div className="flex flex-col gap-1">
      <div
        className={`bg-white border rounded-[12px] p-4 flex items-center gap-4 ${conflictEmail ? 'border-[#B3261E]' : 'border-[#DCDDE0]'}`}
      >
        {/* User icon */}
        <div className="bg-neutral-90 rounded-full p-2 shrink-0">
          <UserGroupIcon className="w-6 h-6 text-on-surface-variant" />
        </div>

        {/* Name + ID */}
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <p className="text-bodyMedium text-neutral-10 break-words">
            {user.name}
          </p>
          <div className="flex items-center gap-1">
            <IdentificationIcon className="w-4 h-4 text-outline shrink-0" />
            <span className="text-bodySmall text-outline">
              {user.user_id ?? '—'}
            </span>
          </div>
        </div>

        {/* Status toggle */}
        {(() => {
          const effectivelyDisabled = isToggleDisabled || !canToggle;
          const tooltipContent =
            isToggleDisabled && toggleDisabledReason
              ? toggleDisabledReason
              : !canToggle
                ? plLabels.common.noPermission
                : null;

          const toggleBtn = (
            <div style={{ pointerEvents: 'none' }}>
              <ToggleButton checked={isActive} onChange={() => {}} />
            </div>
          );

          return (
            <div
              data-testid={`employee-status-toggle-${index}`}
              className={`flex items-center gap-2 shrink-0 ${
                effectivelyDisabled
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-pointer'
              }`}
              onClick={
                !effectivelyDisabled
                  ? (e) => {
                      e.stopPropagation();
                      onToggle?.(user);
                    }
                  : undefined
              }
            >
              {tooltipContent ? (
                <Tooltip
                  content={tooltipContent}
                  forceShow
                  triggerWrapContent
                  contentMinWidth={200}
                  side="bottom"
                  align="center"
                >
                  {toggleBtn}
                </Tooltip>
              ) : (
                toggleBtn
              )}
              <span
                className="text-sm w-[76px] shrink-0"
                style={{ color: '#181A1B', fontWeight: 400 }}
              >
                {isActive
                  ? FACILITY_LABELS.STATUS_ACTIVE
                  : FACILITY_LABELS.STATUS_INACTIVE}
              </span>
            </div>
          );
        })()}

        {/* Three-dot menu — always visible; Edit item disabled when canEdit=false */}
        <button
          ref={buttonRef}
          type="button"
          className="w-10 h-10 flex items-center justify-center rounded-full cursor-pointer hover:bg-[#474A4C]/[0.08] shrink-0"
          data-testid={`employee-menu-button-${index}`}
          aria-label={`Więcej opcji dla ${user.name}`}
          onClick={() => (isMenuOpen ? setIsMenuOpen(false) : openMenu())}
        >
          <MoreVertical size={20} className="text-on-surface-variant" />
        </button>

        {isMenuOpen &&
          createPortal(
            <div
              ref={dropdownRef}
              className="fixed z-[9999]"
              style={{ top: dropdownPos.top, right: dropdownPos.right }}
            >
              <Menu
                items={[
                  {
                    id: 'edit',
                    label: plLabels.common.edit,
                    disabled: !canEdit,
                    ...(!canEdit && {
                      disabledTooltip: plLabels.common.noPermission,
                      disabledTooltipSide: 'left' as const,
                    }),
                  },
                ]}
                size={MenuSize.Small}
                showLeadingIcons={false}
                showTrailingArrows={false}
                toolTip={false}
                onItemClick={() => {
                  setIsMenuOpen(false);
                  onEdit?.();
                }}
              />
            </div>,
            document.body
          )}
      </div>
      {conflictEmail && (
        <p className="text-xs text-[#B3261E] leading-4 tracking-[0.4px] px-1">
          {EMPLOYEE_ERROR_MESSAGES.EMAIL_CONFLICT(conflictEmail)}
        </p>
      )}
    </div>
  );
};

// ─── EmployeeList ──────────────────────────────────────────────────────────────

export const EmployeeList = ({
  facilityId,
  refreshKey,
  resetKey,
  canEdit = false,
  canToggle = false,
  isToggleDisabled = false,
  toggleDisabledReason,
  isValid,
  onTotalCountChange,
  onAccessDenied,
  onEmployeeEdited,
  onEditingChange,
  onSavingChange,
  conflictsByEmployeeId,
  disabled = false,
}: EmployeeListProps) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [items, setItems] = useState<FacilityUserListItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [editingEmployee, setEditingEmployee] =
    useState<FacilityUserListItem | null>(null);
  const [isEditSuccessOpen, setIsEditSuccessOpen] = useState(false);
  const [isEditErrorOpen, setIsEditErrorOpen] = useState(false);
  const [editErrorMessage, setEditErrorMessage] = useState('');

  const [togglingEmployee, setTogglingEmployee] =
    useState<FacilityUserListItem | null>(null);
  const [isToggleSubmitting, setIsToggleSubmitting] = useState(false);
  const [isToggleSuccessOpen, setIsToggleSuccessOpen] = useState(false);
  const [toggleSuccessMessage, setToggleSuccessMessage] = useState('');
  const [isToggleErrorOpen, setIsToggleErrorOpen] = useState(false);
  const [toggleErrorMessage, setToggleErrorMessage] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset list on search/facilityId/refreshKey change
  useEffect(() => {
    setItems([]);
    setPage(1);
    setHasNextPage(false);
  }, [debouncedSearch, facilityId, refreshKey]);

  // Clear inline edit form without re-fetching
  useEffect(() => {
    setEditingEmployee(null);
    onEditingChange?.(false);
  }, [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch current page
  useEffect(() => {
    if (!facilityId) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    const params = debouncedSearch
      ? {
          facilityId,
          page,
          limit: LIMIT,
          search: debouncedSearch,
          ...(isValid !== undefined && { isValid }),
        }
      : {
          facilityId,
          page,
          limit: LIMIT,
          ...(isValid !== undefined && { isValid }),
        };

    listFacilityUsers(params)
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result.accessDenied) {
          onAccessDenied?.();
          return;
        }
        if (result.success && result.data) {
          const incoming = result.data.data;
          setItems((prev) => (page === 1 ? incoming : [...prev, ...incoming]));
          setHasNextPage(result.data.pagination.hasNextPage);
          if (page === 1 && !debouncedSearch) {
            onTotalCountChange?.(result.data.pagination.totalCount);
          }
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    facilityId,
    page,
    debouncedSearch,
    refreshKey,
    isValid,
    onTotalCountChange,
    onAccessDenied,
  ]);

  // Infinite scroll sentinel
  const loadMore = useCallback(() => {
    if (!isLoading && hasNextPage) {
      setPage((prev) => prev + 1);
    }
  }, [isLoading, hasNextPage]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { root: scrollContainerRef.current, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  const handleEditSaved = (updated: FacilityUserListItem) => {
    setItems((prev) =>
      prev.map((item) => (item.id === updated.id ? updated : item))
    );
  };

  const handleToggleConfirm = async () => {
    if (!togglingEmployee) {
      return;
    }
    const isCurrentlyActive = togglingEmployee.status === 'active';
    setIsToggleSubmitting(true);
    const result = await updateEmployeeStatusApiService.updateEmployeeStatus({
      employeeId: togglingEmployee.id,
      isActive: !isCurrentlyActive,
    });
    setIsToggleSubmitting(false);
    if (result.success) {
      setItems((prev) =>
        prev.map((item) =>
          item.id === togglingEmployee.id
            ? { ...item, status: isCurrentlyActive ? 'inactive' : 'active' }
            : item
        )
      );
      setToggleSuccessMessage(
        isCurrentlyActive
          ? TL.DEACTIVATE_EMPLOYEE_SUCCESS.polish
          : TL.ACTIVATE_EMPLOYEE_SUCCESS.polish
      );
      setIsToggleSuccessOpen(true);
      setTogglingEmployee(null);
      onEmployeeEdited?.();
    } else {
      setToggleErrorMessage(
        result.error ?? 'Nie udało się zmienić statusu pracownika.'
      );
      setIsToggleErrorOpen(true);
      setTogglingEmployee(null);
    }
  };

  if (!facilityId) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-10">
        <div className="bg-neutral-90 rounded-full p-4">
          <UserGroupIcon className="w-10 h-10 text-on-surface-variant" />
        </div>
        <div className="flex flex-col items-center gap-1 text-center">
          <p className="text-sm font-medium text-[#474A4C] leading-5">
            Brak pracowników
          </p>
          <p className="text-xs text-[#878B8E] leading-4 tracking-[0.4px]">
            Zapisz placówkę, aby móc dodawać pracowników.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {editingEmployee !== null ? (
        <EditEmployeeForm
          employee={editingEmployee}
          canToggle={canToggle}
          isToggleDisabled={isToggleDisabled}
          toggleDisabledReason={toggleDisabledReason}
          onCancel={() => {
            setEditingEmployee(null);
            onEditingChange?.(false);
          }}
          onSaved={(updated) => {
            handleEditSaved(updated);
            setEditingEmployee(null);
            setIsEditSuccessOpen(true);
            onEmployeeEdited?.();
            onEditingChange?.(false);
          }}
          onToggled={(updated) => {
            handleEditSaved(updated);
            onEmployeeEdited?.();
          }}
          onError={(message) => {
            setEditErrorMessage(message);
            setIsEditErrorOpen(true);
          }}
          onDirtyChange={(isDirty) => onEditingChange?.(isDirty)}
          {...(onSavingChange && { onSavingChange })}
        />
      ) : (
        <div
          className={`flex flex-col gap-4${disabled ? ' pointer-events-none opacity-50' : ''}`}
        >
          {(items.length > 0 || debouncedSearch !== '' || isLoading) && (
            <div
              className={
                isLoading && items.length === 0 ? 'pointer-events-none' : ''
              }
            >
              <SearchBar
                value={search}
                onChange={setSearch}
                placeholder="Szukaj po nazwie"
              />
            </div>
          )}

          {/* Skeleton — shown on initial load and while searching */}
          {isLoading && items.length === 0 && (
            <div className="flex flex-col gap-4">
              {Array.from({ length: 1 }).map((_, idx) => (
                <div
                  key={idx}
                  className="bg-white border border-[#DCDDE0] rounded-xl p-4 flex items-center gap-4"
                >
                  <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse shrink-0" />
                  <div className="flex flex-col gap-2 flex-1 min-w-0">
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
                  </div>
                  <div className="w-20 h-8 rounded-full bg-gray-200 animate-pulse shrink-0" />
                  <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse shrink-0" />
                </div>
              ))}
            </div>
          )}

          {/* User cards */}
          {items.length > 0 && (
            <div
              ref={scrollContainerRef}
              className="flex flex-col gap-4 overflow-y-auto pr-1"
              style={{ maxHeight: MAX_HEIGHT }}
            >
              {items.map((user, i) => (
                <UserCard
                  key={user.id}
                  user={user}
                  index={i}
                  conflictEmail={conflictsByEmployeeId?.[user.id]}
                  canEdit={canEdit}
                  canToggle={canToggle}
                  isToggleDisabled={isToggleDisabled}
                  toggleDisabledReason={toggleDisabledReason}
                  onEdit={() => setEditingEmployee(user)}
                  onToggle={(u) => setTogglingEmployee(u)}
                />
              ))}
              {hasNextPage && (
                <div ref={sentinelRef} className="h-1 shrink-0" />
              )}
              {isLoading &&
                Array.from({ length: 2 }).map((_, idx) => (
                  <div
                    key={`shimmer-${idx}`}
                    className="bg-white border border-[#DCDDE0] rounded-xl p-4 flex items-center gap-4 shrink-0"
                  >
                    <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse shrink-0" />
                    <div className="flex flex-col gap-2 flex-1 min-w-0">
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                      <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
                    </div>
                    <div className="w-20 h-8 rounded-full bg-gray-200 animate-pulse shrink-0" />
                    <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse shrink-0" />
                  </div>
                ))}
            </div>
          )}

          {/* No results */}
          {!isLoading && items.length === 0 && debouncedSearch && (
            <div className="flex flex-col items-center w-full gap-5.5">
              <LottieAnimation
                animationData={noResultsAnimation}
                width={240}
                aspectRatio="240 / 194"
              />
              <div className="flex flex-col items-center gap-1 w-full">
                <p className="w-full text-center text-[#474A4C] text-sm font-medium leading-5 tracking-[0.25px]">
                  Nie znaleziono wyników.
                </p>
                <p className="w-full text-center text-[#474A4C] text-xs font-normal leading-4 tracking-[0.4px]">
                  Spróbuj zmienić kryteria wyszukiwania.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
      <Snackbar
        open={isEditSuccessOpen}
        message={FACILITY_LABELS.EDIT_EMPLOYEE_SUCCESS}
        variant="success"
        onClose={() => setIsEditSuccessOpen(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={isEditErrorOpen}
        message={editErrorMessage}
        variant="error"
        onClose={() => setIsEditErrorOpen(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={isToggleSuccessOpen}
        message={toggleSuccessMessage}
        variant="success"
        onClose={() => setIsToggleSuccessOpen(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={isToggleErrorOpen}
        message={toggleErrorMessage}
        variant="error"
        onClose={() => setIsToggleErrorOpen(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      {togglingEmployee !== null &&
        (() => {
          const isCurrentlyActive = togglingEmployee.status === 'active';
          const config = getEmployeeStatusDialogConfig(
            togglingEmployee.name,
            isCurrentlyActive
          );
          return (
            <UpdateStatusDialog
              open={true}
              onClose={() => setTogglingEmployee(null)}
              onConfirm={() => void handleToggleConfirm()}
              isSubmitting={isToggleSubmitting}
              {...config}
            />
          );
        })()}
    </>
  );
};
